/* The presentation model for registering a source with its own credentials.
 *
 * Pure functions over the API's own connector catalogue. Nothing here holds a credential value: the
 * form's state does, briefly, and these functions decide only *which* fields to render and *how*.
 *
 * The three rules worth not reversing are all about telling a reader the truth about a source family:
 *
 * 1. **An unavailable provider is shown and explained**, never hidden. Hiding it makes the gap
 *    invisible; offering it and failing at first harvest — minutes later, asynchronously — is worse.
 * 2. **Harvest, rules and profiling are three separate facts.** Two families have the first without
 *    the others, so one "supported" badge would either promise rule execution this build cannot do or
 *    hide a harvest it can.
 * 3. **Whether a field is secret comes from the API.** `secret_fields` is on the wire precisely so a
 *    screen does not decide by matching on the word "password".
 */

import type { ConnectorProfile, ConnectorType } from '@/shared/api/generated/catalog';

/** Which authentication mechanism a provider offering a choice is being registered with.
 *
 * Only Snowflake offers one today. Modelled as a toggle rather than two boxes because the domain
 * refuses both-and-neither: a form with a password box and a key box invites filling in both, which is
 * a 422 the caller has to decode.
 */
export type Mechanism = 'password' | 'private_key';

/** A field the form should render, with everything needed to render it correctly. */
export interface CredentialField {
  readonly name: string;
  readonly required: boolean;
  readonly secret: boolean;
  /** A PEM block, which is the one credential that does not fit on a single line. */
  readonly multiline: boolean;
}

/** The two mechanism-specific fields, named so `fieldsFor` can filter on them.
 *
 * Hardcoded rather than derived, and the reason is that the *pairing* is not on the wire:
 * `optional_fields` lists both because each is individually optional, and nothing in the payload says
 * they are alternatives. The API's own comment records that a flat list cannot express "one of these
 * two", so the screen holds the pairing and `SnowflakeCredentials.__post_init__` is what enforces it.
 */
const MECHANISM_FIELDS: Readonly<Record<Mechanism, string>> = {
  password: 'password',
  private_key: 'private_key_pem',
};

/** Fields to render for a provider, in a stable order, honouring the mechanism choice.
 *
 * Required first, then optional, because that is the order somebody fills a form in and it puts the
 * fields that block Submit above the fold.
 *
 * When a provider offers a mechanism choice, the *other* mechanism's field is omitted entirely rather
 * than disabled. A disabled input still reads as something to fill in, and a value left in it before
 * the toggle flipped would be submitted alongside the one now in use — which the domain refuses.
 */
export function fieldsFor(
  profile: ConnectorProfile,
  mechanism: Mechanism,
): readonly CredentialField[] {
  const secret = new Set(profile.secret_fields);
  const alternatives = new Set(Object.values(MECHANISM_FIELDS));
  const chosen = MECHANISM_FIELDS[mechanism];

  const describe = (name: string, required: boolean): CredentialField => ({
    name,
    /* A mechanism field is optional on the wire and mandatory in practice once chosen: exactly one of
     * the pair must be present, so the one on screen is the one that must be filled. Marking it
     * optional would let Submit fire into a 422 the domain has already decided. */
    required: required || name === chosen,
    secret: secret.has(name),
    multiline: name === 'private_key_pem',
  });

  const required = profile.required_fields.map((name) => describe(name, true));
  const optional = profile.optional_fields
    .filter((name) => !alternatives.has(name) || name === chosen)
    .map((name) => describe(name, false));

  return [...required, ...optional];
}

/** Whether this provider asks the caller to choose an authentication mechanism. */
export function offersMechanismChoice(profile: ConnectorProfile): boolean {
  const optional = new Set(profile.optional_fields);
  return Object.values(MECHANISM_FIELDS).every((field) => optional.has(field));
}

/** Whether a provider can be registered at all on this deployment.
 *
 * A family with no metadata connector is refused with a 422, so the form disables Submit and says why
 * rather than letting somebody fill in six fields to be told no.
 */
export function isRegisterable(profile: ConnectorProfile): boolean {
  return profile.harvestable && profile.required_fields.length > 0;
}

/** The default port to suggest for a family that needs an endpoint.
 *
 * A suggestion and not a constraint — the field stays editable. Absent for a family that needs no
 * endpoint at all, where the form renders no host/port/database at any value.
 */
export function defaultPortFor(connector: ConnectorType): number | null {
  switch (connector) {
    case 'postgresql':
      return 5432;
    case 'mysql':
      return 3306;
    case 'oracle':
      return 1521;
    case 'sqlserver':
      return 1433;
    /* Both HTTPS statement APIs. The form does not render a port for either — `needs_endpoint` is
     * false — so this is what a future family reaching that branch would want, not a value anybody
     * sees today. */
    case 'snowflake':
    case 'databricks':
    case 'bigquery':
      return 443;
  }
}

/** Which required fields are still empty, so Submit can say what is missing rather than doing nothing.
 *
 * Names, never values. The caller renders them through the copy catalogue's field labels, so this
 * returns the API's own field names and no English.
 */
export function missingFields(
  fields: readonly CredentialField[],
  values: Readonly<Record<string, string>>,
): readonly string[] {
  return fields
    .filter((field) => field.required && (values[field.name] ?? '').trim() === '')
    .map((field) => field.name);
}

/** The credential object to submit: trimmed, empties dropped, nothing else added.
 *
 * **Empties are dropped rather than sent as `""`.** The API refuses an unknown field and would also
 * refuse a blank required one — but an *optional* field sent as an empty string is a stored empty
 * value where the caller meant "not set", and `_optional` on the server treats blank as absent
 * anyway. Dropping it here means the sealed material holds only what was actually supplied.
 */
export function credentialPayload(
  fields: readonly CredentialField[],
  values: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> {
  const payload: Record<string, string> = {};
  for (const field of fields) {
    const value = (values[field.name] ?? '').trim();
    if (value !== '') payload[field.name] = value;
  }
  return payload;
}
